import React, { useEffect, useMemo, useState } from "react";
import { api, brl, sortProducts } from "@/api";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Search, UserPlus, PackagePlus, ChevronDown, ChevronUp, X, Phone, ScanLine, Package, Printer } from "lucide-react";
import { toast } from "sonner";
import Receipt from "@/components/Receipt";
import ThermalReceipt from "@/components/ThermalReceipt";
import TradeInTerm from "@/components/TradeInTerm";
import InstallmentPlanModal from "@/components/InstallmentPlanModal";
import CurrencyInput from "@/components/CurrencyInput";
import VariantPicker from "@/components/VariantPicker";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import BarcodeScanner from "@/components/BarcodeScanner";
import LeadSelector from "@/components/LeadSelector";
import { useAuth } from "@/AuthContext";

// Label exibido pra "Pendente programado" — renomeado para "Crediário"
// (valor interno preservado por compatibilidade c/ dados existentes).
const labelPayment = (m) => (m === "Pendente programado" ? "Crediário" : m);

const PAYMENTS = ["PIX", "Cartão de Crédito", "Cartão de Débito", "Dinheiro", "Rifa", "Outros", "Pendente", "Pendente programado"];

const blankProduct = { name: "", price: 0, cost: 0, codes: [], supplier_id: null, supplier_note: "", supplier_purchase_date: null };
const blankCustomer = { name: "", phone: "", cpf: "", address: "" };

export default function Vendas() {
  const { canSeeFinance } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [payment, setPayment] = useState("PIX");
  const [discount, setDiscount] = useState(0);
  // ── Pagamento avançado (combinado / troca / taxas) ───────────────────
  // Quando `payments` está vazio, mantém o comportamento legado com `payment` único.
  // Quando o usuário clica "Combinar formas", populamos com pelo menos 1 linha.
  const [payments, setPayments] = useState([]); // [{method, amount}]
  const [tradeIn, setTradeIn] = useState(null); // null | {product_name,brand,code,condition,value,notes,category_id,add_to_stock}
  // Flag pra saber se o modo combinado atual foi disparado pela troca
  // (auto). Isso permite: (a) sincronizar as linhas quando o valor da troca
  // muda, e (b) reverter automaticamente quando a troca é removida/zerada.
  const [tradeAutoApplied, setTradeAutoApplied] = useState(false);
  const [fees, setFees] = useState([]);         // [{label, amount}]
  // ── Pendente Programado (crediário) ──────────────────────────────────
  // Plano de parcelamento criado pelo modal. Quando presente, é enviado
  // junto com a venda. Identifica o subtotal pendente programado nos
  // payments (linhas method === "Pendente programado").
  const [installmentPlan, setInstallmentPlan] = useState(null); // { kind, installments[] }
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [store, setStore] = useState({});
  const [showReceipt, setShowReceipt] = useState(false);
  const [showTradeTerm, setShowTradeTerm] = useState(false);
  const [showThermal, setShowThermal] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState(blankProduct);
  const [productBusy, setProductBusy] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState(blankCustomer);
  const [customerBusy, setCustomerBusy] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [expandedItems, setExpandedItems] = useState({}); // { [key]: true } controla quais linhas têm ajustes abertos
  const [variantTarget, setVariantTarget] = useState(null);
  const [confirmation, setConfirmation] = useState(null); // { title, description } | null
  const [scanOpen, setScanOpen] = useState(false);
  const [scanAvulsoOpen, setScanAvulsoOpen] = useState(false);
  const [scanCartTarget, setScanCartTarget] = useState(null); // { product_id, key } | null
  const [avulsoCodeDraft, setAvulsoCodeDraft] = useState("");
  const [codeInput, setCodeInput] = useState("");
  // Mobile/PWA: lista de produtos inicia RECOLHIDA para reduzir poluição
  // visual. Persistimos a preferência do usuário no localStorage.
  // Quando o usuário digita no filtro, expandimos automaticamente; quando
  // limpa o filtro, voltamos ao estado MANUAL (a menos que ele tenha
  // clicado em "Mostrar Produtos").
  const SHOW_PRODUCTS_KEY = "gestaoexpert:pdv:show_products";
  const FULL_NAME_KEY = "gestaoexpert:pdv:full_name";
  const [showProductsManual, setShowProductsManual] = useState(() => {
    try { return localStorage.getItem(SHOW_PRODUCTS_KEY) === "1"; } catch { return false; }
  });
  // showProducts efetivo: ou foi acionado manualmente, ou há algo digitado
  // no campo de busca / filtro de categoria ativo.
  const hasActiveQuery = !!search || (categoryFilter && categoryFilter !== "all");
  const showProducts = showProductsManual || hasActiveQuery;
  const toggleShowProducts = () => {
    setShowProductsManual(prev => {
      const next = !prev;
      try { localStorage.setItem(SHOW_PRODUCTS_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const [fullName, setFullName] = useState(() => {
    try {
      const v = localStorage.getItem(FULL_NAME_KEY);
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  const toggleFullName = () => {
    setFullName(prev => {
      const next = !prev;
      try { localStorage.setItem(FULL_NAME_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const [searchParams] = useSearchParams();
  const fromCrmLeadId = searchParams.get("from_crm");

  useEffect(() => { (async () => {
    const [p, c, s, cat, sup] = await Promise.all([
      api.get("/products", { params: { light: true } }),
      api.get("/customers"), api.get("/store"), api.get("/categories"),
      api.get("/suppliers").catch(()=>({data: []})),
    ]);
    setProducts(p.data); setCustomers(c.data); setStore(s.data); setCategories(cat.data || []); setSuppliers(sup.data || []);
    // Vinda do CRM: pré-seleciona o cliente automaticamente.
    const cidParam = searchParams.get("customer_id");
    if (cidParam) {
      setCustomerId(cidParam);
      toast.info("Cliente pré-selecionado do CRM. Adicione os produtos e finalize.");
    }
  })(); /* eslint-disable-next-line */ }, []);

  // Adiciona um produto AVULSO ao carrinho (não vai para o estoque, sem product_id)
  const submitNewProduct = () => {
    if (!newProduct.name) return toast.error("Nome obrigatório");
    if (Number(newProduct.price) <= 0) return toast.error("Preço deve ser maior que zero");
    setProductBusy(true);
    try {
      const supplier = suppliers.find(s => s.id === newProduct.supplier_id);
      const supplierIsTroca = supplier && /troca/i.test(supplier.name || "");
      const supplierLabel = supplier ? supplier.name : "";
      const noteParts = [];
      if (supplierLabel) noteParts.push(supplierLabel);
      if (supplierIsTroca && newProduct.supplier_note) noteParts.push(newProduct.supplier_note);
      const tag = noteParts.length ? ` [${noteParts.join(" - ")}]` : "";
      const key = `avulso-${Date.now()}`;
      setCart(prev => [...prev, {
        key,
        product_id: null, // sem produto cadastrado
        name: newProduct.name + tag,
        variant_name: null,
        quantity: 1,
        unit_price: Number(newProduct.price),
        unit_cost: Number(newProduct.cost || 0),
        codes: Array.isArray(newProduct.codes) ? newProduct.codes.filter(Boolean) : [],
        code: (Array.isArray(newProduct.codes) && newProduct.codes[0]) || "",
        supplier_id: newProduct.supplier_id || null,
        supplier_note: supplierIsTroca ? (newProduct.supplier_note || "") : "",
        supplier_purchase_date: newProduct.supplier_id ? (newProduct.supplier_purchase_date || null) : null,
      }]);
      toast.success("Produto avulso adicionado");
      setCreatingProduct(false);
      setNewProduct(blankProduct);
    } finally { setProductBusy(false); }
  };

  const submitNewCustomer = async () => {
    if (!newCustomer.name) return toast.error("Nome obrigatório");
    setCustomerBusy(true);
    try {
      const payload = { ...newCustomer };
      // remove campos vazios opcionais para manter DB limpo
      if (!payload.phone) delete payload.phone;
      if (!payload.cpf) delete payload.cpf;
      if (!payload.address) delete payload.address;
      const r = await api.post("/customers", payload);
      setCustomers(prev => [...prev, r.data]);
      setCustomerId(r.data.id);
      toast.success("Cliente criado");
      setCreatingCustomer(false);
      setNewCustomer(blankCustomer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar cliente");
    } finally { setCustomerBusy(false); }
  };

  const catsMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);
  const filtered = useMemo(() => {
    let list = products;
    if (search) {
      const t = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(t));
    }
    if (categoryFilter !== "all") {
      list = list.filter(p => (p.category_id || "none") === categoryFilter);
    }
    return sortProducts(list, catsMap);
  }, [products, search, categoryFilter, catsMap]);

  const isOutOfStock = (p, variantName = null) => {
    if (p.allow_oversell) return false;
    if (variantName) {
      const v = (p.variants || []).find(x => x.name === variantName);
      return Number(v?.quantity || 0) <= 0;
    }
    if ((p.variants || []).length > 0) {
      return (p.variants || []).every(v => Number(v.quantity || 0) <= 0);
    }
    return Number(p.stock || 0) <= 0;
  };

  const addToCart = (p, variantName = null, opts = {}) => {
    const variant = variantName ? p.variants.find(v => v.name === variantName) : null;
    const available = variant ? Number(variant.quantity || 0) : Number(p.stock || 0);
    const allow = !!p.allow_oversell;
    const key = `${p.id}-${variantName || ""}`;
    const existing = cart.find(x => x.key === key);
    const newQty = (existing?.quantity || 0) + 1;
    if (!allow && newQty > available) {
      toast.error(`Sem estoque: "${p.name}${variantName ? ` (${variantName})` : ""}". Ative "Vender sem estoque" no produto se desejar.`);
      return;
    }
    const unit_price = variant ? variant.price : (p.promo_price || p.price);
    const unit_cost = variant ? variant.cost : p.cost;
    // Propaga o código (preenchido só quando o item entrou via leitor de barras/QR)
    const code = opts.code || (Array.isArray(p.codes) && p.codes[0]) || p.code || "";
    // `codes` inicia com o(s) código(s) do produto cadastrado (snapshot). Mudanças aqui
    // NÃO afetam o produto — só ficam salvas nesta venda.
    const initialCodes = Array.isArray(p.codes) ? [...p.codes].filter(Boolean) : (p.code ? [p.code] : []);
    if (existing) setCart(cart.map(x => x.key === key ? { ...x, quantity: x.quantity + 1 } : x));
    else setCart([...cart, { key, product_id: p.id, name: p.name + (variantName ? ` (${variantName})` : ""), variant_name: variantName, quantity: 1, unit_price, unit_cost, code, codes: initialCodes }]);
  };

  // ── Códigos por item (escopo da venda) ────────────────────────────────
  // Estas funções operam APENAS no item do carrinho — NÃO modificam o
  // produto cadastrado. Substitui o fluxo antigo addCodeToCataloguedProduct.
  const addCodeToCartItem = (key, rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    setCart(prev => prev.map(x => {
      if (x.key !== key) return x;
      const list = Array.isArray(x.codes) ? x.codes : (x.code ? [x.code] : []);
      if (list.includes(code)) {
        toast.info(`Código "${code}" já está neste item`);
        return x;
      }
      return { ...x, codes: [...list, code], code: x.code || code };
    }));
    toast.success(`Código "${code}" adicionado`);
  };
  const updateCartItemCode = (key, idx, val) => setCart(prev => prev.map(x => {
    if (x.key !== key) return x;
    const list = Array.isArray(x.codes) ? [...x.codes] : (x.code ? [x.code] : []);
    list[idx] = val;
    return { ...x, codes: list, code: list[0] || "" };
  }));
  const removeCartItemCode = (key, idx) => setCart(prev => prev.map(x => {
    if (x.key !== key) return x;
    const list = Array.isArray(x.codes) ? x.codes.filter((_, i) => i !== idx) : [];
    return { ...x, codes: list, code: list[0] || "" };
  }));

  const updQty = (key, qty) => setCart(cart.map(x => x.key === key ? { ...x, quantity: Math.max(1, qty) } : x));
  const updItemCost = (key, val) => setCart(cart.map(x => x.key === key ? { ...x, unit_cost: Math.max(0, Number(val) || 0) } : x));
  const updItemDiscount = (key, val) => setCart(cart.map(x => x.key === key ? { ...x, item_discount: Math.max(0, Number(val) || 0) } : x));
  const rm = (key) => setCart(cart.filter(x => x.key !== key));

  // ── Códigos do produto AVULSO (no diálogo Novo)
  const addAvulsoCode = (raw) => {
    const c = String(raw || "").trim();
    if (!c) return;
    setNewProduct(p => {
      const list = Array.isArray(p.codes) ? p.codes : [];
      if (list.includes(c)) { toast.info("Esse código já está nesse produto"); return p; }
      toast.success(`Código "${c}" adicionado`);
      return { ...p, codes: [...list, c] };
    });
  };
  const removeAvulsoCode = (idx) => setNewProduct(p => ({ ...p, codes: (p.codes || []).filter((_, i) => i !== idx) }));
  const editAvulsoCode = (idx, val) => setNewProduct(p => ({
    ...p,
    codes: (p.codes || []).map((c, i) => i === idx ? val : c),
  }));

  // ── Adiciona um código novo a um produto JÁ CADASTRADO via fluxo da venda.
  // Útil quando o caixa bipa o produto que acabou de chegar e quer cadastrar o
  // código no catálogo sem sair do PDV. Faz PUT no /api/products/{pid} mantendo
  // o resto do payload do produto intacto.
  const addCodeToCataloguedProduct = async (productId, newCode) => {
    const code = String(newCode || "").trim();
    if (!code || !productId) return;
    const p = products.find(x => x.id === productId);
    if (!p) { toast.error("Produto não encontrado no carrinho"); return; }
    const currentCodes = Array.isArray(p.codes) ? p.codes : (p.code ? [p.code] : []);
    if (currentCodes.includes(code)) {
      toast.info(`O código "${code}" já está nesse produto`);
      return;
    }
    // Busca o produto FULL (lista usa ?light=true sem photos/description)
    let full;
    try { full = (await api.get(`/products/${productId}`)).data; }
    catch { toast.error("Não foi possível carregar o produto"); return; }
    const merged = Array.from(new Set([...(full.codes || []), ...(full.code ? [full.code] : []), code]));
    const payload = { ...full, codes: merged };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at; delete payload._id;
    try {
      await api.put(`/products/${productId}`, payload);
      // Atualiza cache local pra próximas leituras
      setProducts(prev => prev.map(x => x.id === productId ? { ...x, codes: merged, code: merged[0] || "" } : x));
      toast.success(`Código "${code}" adicionado ao produto`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar código no produto");
    }
  };

  // Busca produto por código (barcode/QR) e adiciona ao carrinho.
  // Procura primeiro localmente (instantâneo); cai pro endpoint /products/by-code quando
  // não houver match no cache (ex: produto criado por outro caixa logado em outra aba).
  const handleCodeLookup = async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    // tenta cache local primeiro
    let p = products.find(prod => (Array.isArray(prod.codes) && prod.codes.includes(code)) || prod.code === code);
    if (!p) {
      try {
        const r = await api.get(`/products/by-code/${encodeURIComponent(code)}`);
        p = r.data;
      } catch (e) {
        toast.error(`Nenhum produto com o código "${code}"`);
        return;
      }
    }
    // Produto com variantes: abre o VariantPicker para o usuário escolher
    if ((p.variants || []).length > 0) {
      openVariantPicker(p);
      toast.info(`${p.name} — escolha a variação`);
      return;
    }
    addToCart(p, null, { code });
    toast.success(`+ ${p.name}`);
  };

  // Abre o VariantPicker garantindo que o produto está em modo FULL — a listagem
  // do PDV usa ?light=true que descarta `photo` das variantes (para economizar
  // bandwidth). Sem o full, o picker mostraria a foto principal em vez da foto
  // específica de cada variante.
  const openVariantPicker = async (p) => {
    if (!(p.variants || []).length) return setVariantTarget(p);
    // Se já tem photo em pelo menos uma variante, já está full — abre direto.
    const alreadyFull = (p.variants || []).some(v => v.photo);
    if (alreadyFull) return setVariantTarget(p);
    try {
      const r = await api.get(`/products/${p.id}`);
      setVariantTarget(r.data);
    } catch {
      // Fallback: abre com o que tem (sem foto da variante) em vez de bloquear a venda.
      setVariantTarget(p);
    }
  };

  // Aplica seleção de variantes do VariantPicker ao carrinho (modo "set" — substitui qty existente,
  // não soma — assim o usuário pode editar via picker tantas vezes quiser sem inflacionar).
  const applyVariantSelections = (product, selections) => {
    const newKeys = new Set(selections.map(s => `${product.id}-${s.variant_name}`));
    // Remove do carrinho linhas desse produto que não estão na nova seleção
    const filteredCart = cart.filter(x => !x.product_id || x.product_id !== product.id || newKeys.has(x.key));
    let updated = [...filteredCart];
    selections.forEach(s => {
      const key = `${product.id}-${s.variant_name}`;
      const idx = updated.findIndex(x => x.key === key);
      const row = {
        key,
        product_id: product.id,
        name: `${product.name} (${s.variant_name})`,
        variant_name: s.variant_name,
        quantity: s.quantity,
        unit_price: s.unit_price,
        unit_cost: s.unit_cost,
      };
      if (idx >= 0) updated[idx] = { ...updated[idx], quantity: row.quantity, unit_price: row.unit_price, unit_cost: row.unit_cost };
      else updated.push(row);
    });
    setCart(updated);
    setVariantTarget(null);
    toast.success(selections.length === 1 ? "1 variante adicionada" : `${selections.length} variantes adicionadas`);
  };

  // Mapeia quantidades atuais no carrinho por variant_name (para alimentar o picker)
  const currentVariantQuantities = (productId) => {
    const out = {};
    cart.forEach(it => {
      if (it.product_id === productId && it.variant_name) {
        out[it.variant_name] = it.quantity;
      }
    });
    return out;
  };

  const lineDiscounts = cart.reduce((s, x) => s + Math.max(0, Number(x.item_discount || 0)), 0);
  const subtotalGross = cart.reduce((s, x) => s + x.unit_price * x.quantity, 0);
  const subtotal = subtotalGross - lineDiscounts; // após descontos por item
  const total = Math.max(0, subtotal - Number(discount || 0));

  // Totais derivados do pagamento avançado.
  // - feesTotal: SOMENTE pro recibo / detalhe interno (NÃO afeta lucro/faturamento).
  // - paymentsSum: soma de TODAS as linhas (inclusive "Pendente" explícitas).
  // - paidNow: soma somente das formas efetivamente recebidas (exclui "Pendente").
  // - pendingAmount: o que falta receber (pode ser implícito quando soma < total).
  // - grandTotal: total + taxas (cliente paga). Aparece só no recibo.
  const feesTotal = useMemo(
    () => fees.reduce((s, f) => s + Math.max(0, Number(f.amount || 0)), 0),
    [fees]
  );
  const paymentsSum = useMemo(
    () => payments.reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0),
    [payments]
  );
  const paidNow = useMemo(
    () => payments
      .filter(p => (p.method || "").toLowerCase() !== "pendente")
      .reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0),
    [payments]
  );
  const grandTotal = total + feesTotal;
  // Quando tem combinado: pendência = grandTotal - paidNow (positivo se falta receber).
  // No PDV não exigimos que o usuário adicione linha "Pendente" — auto-detecta.
  const pendingAmount = payments.length > 0 ? Math.max(0, grandTotal - paidNow) : 0;
  // Tolerância de R$ 0,01 para casar com paymentsOverpay e evitar drift de ponto flutuante.
  const hasPending = pendingAmount > 0.01;  // Só é erro se a soma das formas ULTRAPASSAR o total (sobra recebida).
  const paymentsOverpay = payments.length > 0 && (paymentsSum - grandTotal > 0.01);
  const paymentsMatch = !paymentsOverpay;

  // Valor a ser parcelado (Pendente programado):
  // - se há linhas "Pendente programado" → soma delas
  // - caso contrário, se o método único é "Pendente programado" → todo o grandTotal
  // - 0 quando não há programação
  const programmedTotal = useMemo(() => {
    if (payments.length > 0) {
      return payments
        .filter(p => (p.method || "").toLowerCase() === "pendente programado")
        .reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0);
    }
    if ((payment || "").toLowerCase() === "pendente programado") return grandTotal;
    return 0;
  }, [payments, payment, grandTotal]);

  // Quando o usuário muda o método/payments e o programmedTotal cair pra 0,
  // limpa o plano (evita enviar plano "órfão" sem valor associado).
  useEffect(() => {
    if (programmedTotal <= 0 && installmentPlan) setInstallmentPlan(null);
    // eslint-disable-next-line
  }, [programmedTotal]);

  // ── Troca → auto-combinar formas de pagamento ─────────────────────────
  // Quando o usuário informa um valor de troca:
  //   - Se está em modo simples, ativa combinado com 2 linhas:
  //       [ Troca: <valor>, <método atual>: total - valor ]
  //   - Se já está em combinado, insere/atualiza a linha "Troca" e ajusta
  //     a primeira linha não-Troca para absorver a diferença.
  // Ao remover ou zerar a troca, a linha "Troca" é retirada e (se restar
  // apenas 1 linha) volta ao modo simples com esse método.
  const isTrocaLine = (p) => (p.method || "").toLowerCase() === "troca";
  useEffect(() => {
    const tv = Number(tradeIn?.value || 0);
    const tradeExists = !!tradeIn;

    if (!tradeExists || tv <= 0) {
      if (tradeAutoApplied) {
        setPayments(prev => {
          const filtered = prev.filter(p => !isTrocaLine(p));
          if (filtered.length <= 1) {
            if (filtered.length === 1 && filtered[0].method) setPayment(filtered[0].method);
            return [];
          }
          return filtered;
        });
        setTradeAutoApplied(false);
      }
      return;
    }

    if (!tradeAutoApplied) {
      setPayments(prev => {
        if (prev.length === 0) {
          const remainder = Math.max(0, total - tv);
          return [
            { method: "Troca", amount: Number(tv.toFixed(2)) },
            { method: payment, amount: Number(remainder.toFixed(2)) },
          ];
        }
        if (prev.some(isTrocaLine)) return prev;
        const arr = [{ method: "Troca", amount: Number(tv.toFixed(2)) }, ...prev];
        for (let i = 1; i < arr.length; i++) {
          if (!isTrocaLine(arr[i])) {
            arr[i] = { ...arr[i], amount: Math.max(0, Number(arr[i].amount || 0) - tv) };
            break;
          }
        }
        return arr;
      });
      setTradeAutoApplied(true);
      return;
    }

    // Sync: valor da troca mudou — atualiza linha Troca e recomputa a
    // primeira linha não-Troca como total - (Troca + demais). Recompute
    // evita drift de ponto flutuante causado por deltas incrementais.
    setPayments(prev => {
      const ti = prev.findIndex(isTrocaLine);
      if (ti < 0) return prev;
      const currentTv = Number(prev[ti].amount || 0);
      if (Math.abs(currentTv - tv) < 0.005) return prev;
      const tvRounded = Math.round(tv * 100) / 100;
      const arr = prev.map((p, i) => i === ti ? { ...p, amount: tvRounded } : p);
      // Índice da primeira linha não-Troca (a que absorverá a diferença)
      const targetIdx = arr.findIndex((p, i) => i !== ti && !isTrocaLine(p));
      if (targetIdx < 0) return arr;
      const othersSum = arr.reduce((s, p, i) => (i === targetIdx ? s : s + Number(p.amount || 0)), 0);
      const remainder = Math.max(0, Math.round((total - othersSum) * 100) / 100);
      arr[targetIdx] = { ...arr[targetIdx], amount: remainder };
      return arr;
    });
  }, [tradeIn?.value, tradeIn, total]);

  const submit = async (finalize) => {
    if (cart.length === 0) return toast.error("Carrinho vazio");
    // Erro APENAS se houver excedente. Falta = vai pra "Vendas Pendentes" automático.
    if (payments.length > 0 && paymentsOverpay) {
      return toast.error(`A soma das formas (${brl(paymentsSum)}) ultrapassou o total ${brl(grandTotal)}`);
    }
    // Se há pendência sem cliente, avisamos — sem cliente não dá pra registrar saldo.
    if (payments.length > 0 && hasPending && !customerId) {
      return toast.error("Há valor pendente — selecione um cliente para registrar em Vendas Pendentes.");
    }
    // Programado precisa de plano + cliente
    if (programmedTotal > 0) {
      if (!customerId) return toast.error("Crediário requer cliente — selecione um cliente.");
      if (!installmentPlan) return toast.error("Configure o parcelamento — clique em 'Configurar parcelas'.");
    }
    const items = cart.map(({ key, ...rest }) => rest);
    // Limpa payments / fees vazios; trade-in só vai se tem nome de produto
    const cleanPayments = payments
      .map(p => ({ method: (p.method || "").trim(), amount: Number(p.amount || 0), note: p.note || "" }))
      .filter(p => p.method && p.amount > 0);
    const cleanFees = fees
      .map(f => ({ label: (f.label || "").trim(), amount: Number(f.amount || 0) }))
      .filter(f => f.label && f.amount > 0);
    const cleanTradeIn = tradeIn && (tradeIn.product_name || "").trim()
      ? { ...tradeIn, value: Number(tradeIn.value || 0), product_name: tradeIn.product_name.trim() }
      : null;
    try {
      const r = await api.post("/sales", {
        customer_id: customerId, items,
        payment_method: payment, discount: Number(discount || 0),
        note, show_note_on_receipt: showNote, finalize,
        payments: cleanPayments,
        trade_in: cleanTradeIn,
        fees: cleanFees,
        installment_plan: installmentPlan || null,
        lead_id: leadId || null,
      });
      const customer = customers.find(c => c.id === customerId);
      setLastSale({ ...r.data, customer });
      if (finalize) {
        setConfirmation({ title: "Venda finalizada!", description: `${cart.length} ${cart.length === 1 ? "item" : "itens"} processado(s).`, canPrint: true });
        setShowReceipt(true);
      } else {
        setConfirmation({ title: "Pedido salvo", description: "Você pode encontrá-lo em Pedidos." });
      }
      // Reset
      setCart([]); setDiscount(0); setNote(""); setShowNote(false);
      setPayments([]); setTradeIn(null); setFees([]); setInstallmentPlan(null);
      setTradeAutoApplied(false);
      setLeadId(null);
      // refresh
      const [p, c] = await Promise.all([api.get("/products", { params: { light: true } }), api.get("/customers")]);
      setProducts(p.data); setCustomers(c.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar venda");
    }
  };

  return (
    <div className="space-y-8" data-testid="vendas-page">
      <div>
        <p className="label-tiny mb-2">Operação</p>
        <h1 className="font-display text-4xl tracking-tight">Nova Venda</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-4 sm:p-6 border-[#E5E5E5] shadow-none min-w-0">
          {/* Linha do leitor de código de barras / QR */}
          <div className="flex gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[160px]">
              <ScanLine size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
              <Input
                value={codeInput}
                onChange={(e)=>setCodeInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); const c = codeInput.trim(); if (c) { handleCodeLookup(c); setCodeInput(""); } } }}
                placeholder="Código (digite ou bipe) — Enter pra adicionar"
                className="pl-9 font-mono"
                data-testid="sale-code-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={()=>setScanOpen(true)}
              data-testid="sale-scan-button"
              title="Abrir câmera para leitura contínua de códigos"
            >
              <ScanLine size={14} className="mr-1"/> Ler código
            </Button>
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[160px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
              <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar produto…" className="pl-9" data-testid="sale-product-search"/>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[170px]" data-testid="sale-category-filter"><SelectValue placeholder="Categoria"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas categorias</SelectItem>
                <SelectItem value="none">Sem categoria</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={()=>setCreatingProduct(true)} data-testid="quick-new-product-button" title="Cadastrar novo produto">
              <PackagePlus size={14} className="mr-1"/> Novo
            </Button>
            <Button
              variant={showProductsManual ? "default" : "outline"}
              onClick={toggleShowProducts}
              className={showProductsManual ? "bg-[#0A0A0A] text-white hover:bg-black" : ""}
              data-testid="toggle-show-products"
              title={showProductsManual ? "Recolher lista de produtos" : "Mostrar lista de produtos"}
            >
              <Package size={14} className="mr-1"/>
              {showProductsManual ? `Ocultar Produtos` : `Mostrar Produtos${filtered.length ? ` (${filtered.length})` : ""}`}
            </Button>
          </div>
          {/* Toggle "Mostrar nome completo" — memorizado em localStorage,
              vem padrão MARCADO. Mostrado só quando a lista está visível
              para não poluir a UI. */}
          {showProducts && (
            <label className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300 mb-2 cursor-pointer select-none" data-testid="full-name-toggle">
              <Checkbox
                checked={fullName}
                onCheckedChange={toggleFullName}
                data-testid="full-name-checkbox"
                className="h-4 w-4"
              />
              <span>Mostrar nome completo</span>
            </label>
          )}
          {showProducts && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
              {filtered.map(p => {
                const productOut = isOutOfStock(p);
                const hasVariants = (p.variants || []).length > 0;
                const variantQs = hasVariants ? currentVariantQuantities(p.id) : {};
                const selectedVariantsCount = Object.values(variantQs).reduce((s, q) => s + Number(q || 0), 0);
                return (
                  <button
                    key={p.id}
                    onClick={()=> hasVariants ? openVariantPicker(p) : addToCart(p)}
                    disabled={productOut}
                    className={`relative text-left border rounded-md p-3 transition ${productOut ? "border-red-300 bg-red-50/40 cursor-not-allowed opacity-70" : "border-[#E5E5E5] hover:bg-neutral-50 dark:hover:bg-white/5 dark:border-white/10"}`}
                    data-testid={`add-to-cart-${p.id}`}
                  >
                    <div className={`text-sm font-medium pr-12 ${fullName ? "break-words" : "truncate"}`} title={p.name}>{p.name}</div>
                    <div className="text-xs text-neutral-500">{brl(p.promo_price || p.price)}</div>
                    <div className={`text-[10px] mt-1 ${productOut ? "text-red-600 font-medium" : "text-neutral-400"}`}>
                      {hasVariants
                        ? `${p.variants.length} ${p.variants.length === 1 ? "opção" : "opções"}${productOut ? " — todas esgotadas" : ""}`
                        : `est: ${p.stock}${productOut ? " — esgotado" : ""}`}
                    </div>
                    {hasVariants && selectedVariantsCount > 0 && (
                      <div className="absolute top-2 right-2 bg-emerald-500 text-white text-[10px] font-bold w-6 h-6 flex items-center justify-center rounded-full shadow-sm" data-testid={`variants-count-${p.id}`}>
                        {selectedVariantsCount}
                      </div>
                    )}
                    {hasVariants && (
                      <div className="text-[10px] mt-1 text-[#1DA1FF]">Toque para selecionar →</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {!showProducts && (
            <p className="text-xs text-neutral-400 italic text-center py-4">
              Lista recolhida. Use a busca/código acima ou toque em <b>Mostrar Produtos</b>.
            </p>
          )}
        </Card>

        <Card className="p-4 sm:p-6 border-[#E5E5E5] shadow-none min-w-0">
          <h3 className="font-display text-lg mb-4">Carrinho</h3>
          <div className="space-y-2 max-h-[360px] overflow-y-auto" data-testid="cart-items">
            {cart.map(it => {
              const lineGross = it.unit_price * it.quantity;
              const lineDisc = Math.max(0, Number(it.item_discount || 0));
              const lineTotal = Math.max(0, lineGross - lineDisc);
              const expanded = !!expandedItems[it.key];
              const hasAdjust = (lineDisc > 0) || (canSeeFinance && it.unit_cost !== undefined);
              return (
                <div key={it.key} className="border border-[#F0F0F0] rounded-md p-2.5 bg-white">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="truncate" title={it.name}>{it.name}</div>
                      <div className="text-xs text-neutral-500 flex flex-wrap gap-x-2">
                        <span>{brl(it.unit_price)}</span>
                        {lineDisc > 0 && <span className="text-red-600">-{brl(lineDisc)}</span>}
                        {lineDisc > 0 && <span className="font-medium text-neutral-700">= {brl(lineTotal)}</span>}
                      </div>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={e=>updQty(it.key, Number(e.target.value))}
                      className="w-16 h-8"
                      data-testid={`cart-qty-${it.key}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={()=>setExpandedItems(prev => ({ ...prev, [it.key]: !prev[it.key] }))}
                      className="h-8 px-1.5"
                      data-testid={`cart-toggle-adjust-${it.key}`}
                      title="Ajustes (custo / desconto)"
                    >
                      {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                    </Button>
                    {it.product_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={()=>setScanCartTarget({ product_id: it.product_id, key: it.key })}
                        className="h-8 px-1.5"
                        title="Bipar e cadastrar mais códigos neste produto"
                        data-testid={`cart-scan-${it.key}`}
                      >
                        <ScanLine size={14}/>
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={()=>rm(it.key)} data-testid={`cart-remove-${it.key}`}><Trash2 size={12}/></Button>
                  </div>
                  {expanded && (
                    <div className="grid grid-cols-2 gap-2 mt-2.5 pt-2.5 border-t border-[#F5F5F5]">
                      {canSeeFinance && (
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 block">Custo desta venda</label>
                          <CurrencyInput
                            value={it.unit_cost || 0}
                            onChange={(v)=>updItemCost(it.key, v)}
                            className="h-8 text-sm"
                            data-testid={`cart-item-cost-${it.key}`}
                          />
                        </div>
                      )}
                      <div className={canSeeFinance ? "" : "col-span-2"}>
                        <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 block">Desconto no item (R$)</label>
                        <CurrencyInput
                          value={it.item_discount || 0}
                          onChange={(v)=>updItemDiscount(it.key, v)}
                          className="h-8 text-sm"
                          data-testid={`cart-item-discount-${it.key}`}
                        />
                      </div>
                      <p className="col-span-2 text-[10px] text-neutral-400 mt-0.5">
                        Estes ajustes valem APENAS nesta venda. O cadastro do produto não é alterado.
                      </p>
                      {/* Códigos de barras desta venda — não modificam o cadastro do produto */}
                      <div className="col-span-2 pt-1.5 border-t border-[#F5F5F5] dark:border-white/10">
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-[10px] uppercase tracking-wider text-neutral-500">Códigos (apenas nesta venda)</label>
                          {it.product_id && (
                            <button
                              type="button"
                              onClick={()=>setScanCartTarget({ product_id: it.product_id, key: it.key })}
                              className="text-[10px] text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1 hover:underline"
                              data-testid={`cart-item-scan-${it.key}`}
                            ><ScanLine size={11}/> Bipar</button>
                          )}
                        </div>
                        <div className="space-y-1">
                          {(Array.isArray(it.codes) ? it.codes : []).map((c, idx) => (
                            <div key={idx} className="flex gap-1.5 items-center" data-testid={`cart-item-code-row-${it.key}-${idx}`}>
                              <Input
                                value={c}
                                onChange={(e)=>updateCartItemCode(it.key, idx, e.target.value)}
                                className="h-7 text-xs font-mono"
                                placeholder="Código"
                                data-testid={`cart-item-code-${it.key}-${idx}`}
                              />
                              <button
                                type="button"
                                onClick={()=>removeCartItemCode(it.key, idx)}
                                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded p-1"
                                title="Remover"
                              ><X size={11}/></button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="text-[10px] text-neutral-500 hover:text-neutral-700 inline-flex items-center gap-1"
                            data-testid={`cart-item-code-add-${it.key}`}
                            onClick={()=>{
                              const c = window.prompt("Código de barras:");
                              if (c && c.trim()) addCodeToCartItem(it.key, c.trim());
                            }}
                          >
                            <Plus size={10}/> Adicionar código manualmente
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {cart.length === 0 && <div className="text-xs text-neutral-400">Vazio</div>}
          </div>
          <div className="border-t border-[#E5E5E5] mt-4 pt-4 space-y-3">
            <div>
              <Label>Cliente</Label>
              {(() => {
                const selected = customerId ? customers.find(c => c.id === customerId) : null;
                if (selected) {
                  return (
                    <div className="flex gap-2 mt-1.5">
                      <div
                        className="flex-1 flex items-center gap-3 px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/15 dark:border-emerald-800/50"
                        data-testid="sale-customer-selected"
                      >
                        <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-display flex-shrink-0">
                          {(selected.name || "?").trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{selected.name}</div>
                          {selected.phone && (
                            <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate flex items-center gap-1">
                              <Phone size={10}/> {selected.phone}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={()=>{ setCustomerId(null); setCustomerSearch(""); }}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-red-600 hover:bg-white/60 transition"
                          data-testid="sale-customer-clear"
                          title="Trocar cliente"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                      <Button variant="outline" size="icon" onClick={()=>setCreatingCustomer(true)} data-testid="quick-new-customer-button" title="Cadastrar novo cliente">
                        <UserPlus size={14}/>
                      </Button>
                    </div>
                  );
                }
                const t = customerSearch.trim().toLowerCase();
                const filtered = t
                  ? customers.filter(c =>
                      (c.name || "").toLowerCase().includes(t) ||
                      (c.phone || "").toLowerCase().includes(t) ||
                      (c.cpf || "").toLowerCase().includes(t)
                    ).slice(0, 8)
                  : [];
                return (
                  <div className="flex gap-2 mt-1.5">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"/>
                      <Input
                        value={customerSearch}
                        onChange={(e)=>setCustomerSearch(e.target.value)}
                        placeholder="Buscar por nome, telefone, CPF…"
                        className="pl-9"
                        data-testid="sale-customer-search"
                      />
                      {t && filtered.length > 0 && (
                        <div
                          className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-[#E5E5E5] bg-white dark:bg-[#0F1626] dark:border-white/10 shadow-lg max-h-[260px] overflow-y-auto"
                          data-testid="sale-customer-list"
                        >
                          {filtered.map(c => (
                            <button
                              type="button"
                              key={c.id}
                              onClick={()=>{ setCustomerId(c.id); setCustomerSearch(""); }}
                              className="w-full text-left px-3 py-2 hover:bg-neutral-50 dark:hover:bg-white/5 flex items-center gap-3 border-b border-[#F4F4F4] dark:border-white/5 last:border-0"
                              data-testid={`sale-customer-option-${c.id}`}
                            >
                              <div className="h-7 w-7 rounded-full bg-neutral-200 dark:bg-white/10 text-neutral-600 dark:text-white/80 flex items-center justify-center text-[11px] font-display flex-shrink-0">
                                {(c.name || "?").trim().charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{c.name}</div>
                                {(c.phone || c.cpf) && (
                                  <div className="text-[11px] text-neutral-500 truncate">
                                    {c.phone}{c.phone && c.cpf ? " · " : ""}{c.cpf}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {t && filtered.length === 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-[#E5E5E5] bg-white dark:bg-[#0F1626] dark:border-white/10 shadow-lg px-3 py-3 text-xs text-neutral-400 text-center">
                          Nenhum cliente encontrado.
                        </div>
                      )}
                    </div>
                    <Button variant="outline" size="icon" onClick={()=>setCreatingCustomer(true)} data-testid="quick-new-customer-button" title="Cadastrar novo cliente">
                      <UserPlus size={14}/>
                    </Button>
                  </div>
                );
              })()}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Forma de pagamento</Label>
                <button
                  type="button"
                  onClick={() => {
                    if (payments.length === 0) {
                      // Inicia combinação com o método atual já lançado
                      setPayments([{ method: payment, amount: Number(total.toFixed(2)) }, { method: "Dinheiro", amount: 0 }]);
                    } else {
                      // Volta pro modo simples
                      setPayments([]);
                      setTradeAutoApplied(false);
                    }
                  }}
                  className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                  data-testid="toggle-combined-payment"
                >
                  {payments.length === 0 ? <><Plus size={11}/> Combinar + formas</> : <><X size={11}/> Voltar p/ pagamento simples</>}
                </button>
              </div>
              {payments.length === 0 ? (
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger data-testid="sale-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENTS.map(p => <SelectItem key={p} value={p}>{labelPayment(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2 mt-1.5">
                  {payments.map((p, idx) => (
                    <div key={idx} className="flex gap-2 items-end" data-testid={`payment-row-${idx}`}>
                      <div className="flex-1">
                        <Select value={p.method} onValueChange={(v) => setPayments(prev => prev.map((x, i) => i === idx ? { ...x, method: v } : x))}>
                          <SelectTrigger className="h-9 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            {[...PAYMENTS, "Troca"].map(m => <SelectItem key={m} value={m}>{labelPayment(m)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-36">
                        <CurrencyInput
                          value={p.amount}
                          onChange={(v) => setPayments(prev => prev.map((x, i) => i === idx ? { ...x, amount: v } : x))}
                        />
                      </div>
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => setPayments(prev => prev.filter((_, i) => i !== idx))}
                        className="h-9 w-9 text-red-500"
                        title="Remover"
                      ><Trash2 size={13}/></Button>
                    </div>
                  ))}
                  <div className="flex justify-between items-center gap-2">
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => setPayments(prev => [...prev, { method: "Dinheiro", amount: Math.max(0, grandTotal - paymentsSum) }])}
                      className="h-8 text-xs"
                      data-testid="add-payment-row"
                    ><Plus size={12} className="mr-1"/> Adicionar forma</Button>
                    <div className="text-[11px] font-mono text-right">
                      <div>
                        <span className="text-neutral-500">Recebido: </span>
                        <span className={paymentsOverpay ? "text-red-600" : "text-emerald-600"}>{brl(paidNow)}</span>
                        <span className="text-neutral-400"> / {brl(grandTotal)}</span>
                      </div>
                      {hasPending && (
                        <div className="text-amber-600 dark:text-amber-400" data-testid="pending-indicator">
                          Pendente: {brl(pendingAmount)}
                        </div>
                      )}
                    </div>
                  </div>
                  {paymentsOverpay && (
                    <div className="text-[10px] text-red-600 -mt-1">A soma das formas ultrapassou o total {brl(grandTotal)}.</div>
                  )}
                  {hasPending && !paymentsOverpay && (
                    <div className="text-[10px] text-amber-600 -mt-1">
                      ⚠ Valor pendente — será registrado automaticamente em <strong>Vendas Pendentes</strong> ao finalizar{customerId ? "." : " (selecione um cliente)."}
                    </div>
                  )}
                </div>
              )}
              {/* Configurador de Pendente programado */}
              {programmedTotal > 0 && (
                <div className="mt-2 border border-purple-200 dark:border-purple-900/40 bg-purple-50/60 dark:bg-purple-950/20 rounded-md p-2.5" data-testid="programmed-config">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-medium text-purple-700 dark:text-purple-300">
                      Crediário: {brl(programmedTotal)}
                    </span>
                    <Button
                      size="sm" variant="outline" type="button"
                      onClick={()=>setPlanModalOpen(true)}
                      className="h-7 text-[11px]"
                      data-testid="programmed-config-btn"
                    >{installmentPlan ? "Editar parcelas" : "Configurar parcelas"}</Button>
                  </div>
                  {installmentPlan ? (
                    <div className="text-[10px] text-purple-700 dark:text-purple-300/90">
                      ✓ {installmentPlan.installments.length}x {installmentPlan.kind === "equal" ? "iguais" : "personalizadas"} — 1ª em {installmentPlan.installments[0]?.due_date}
                    </div>
                  ) : (
                    <div className="text-[10px] text-amber-600">Configure as parcelas antes de finalizar.</div>
                  )}
                </div>
              )}
            </div>

            {/* ── Troca ───────────────────────────────────────────── */}
            <div className="border-t border-[#E5E5E5] dark:border-white/10 pt-3">
              {!tradeIn ? (
                <button
                  type="button"
                  onClick={() => setTradeIn({ product_name: "", brand: "", code: "", condition: "", value: 0, notes: "", category_id: null, add_to_stock: false })}
                  className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                  data-testid="toggle-trade-in"
                ><Plus size={12}/> Receber produto na troca</button>
              ) : (
                <div className="border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 rounded-md p-3 space-y-2" data-testid="trade-in-section">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium uppercase tracking-widest text-amber-700 dark:text-amber-400">Produto recebido na troca</span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (!customerId) {
                            return toast.error("Impossível imprimir.\nCliente deve ser cadastrado antes.");
                          }
                          if (!tradeIn.product_name?.trim()) {
                            return toast.error("Preencha o nome do produto antes de imprimir o termo.");
                          }
                          setShowTradeTerm(true);
                        }}
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                        title="Imprimir termo de troca"
                        data-testid="print-trade-term"
                      >
                        <Printer size={12}/> Imprimir termo troca
                      </button>
                      <button type="button" onClick={() => setTradeIn(null)} className="text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded p-1" title="Cancelar troca" data-testid="remove-trade-in"><X size={12}/></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <Label className="text-[11px]">Nome do produto *</Label>
                      <Input value={tradeIn.product_name} onChange={e => setTradeIn(p => ({...p, product_name: e.target.value}))} className="h-8 text-sm" data-testid="trade-in-name"/>
                    </div>
                    <div>
                      <Label className="text-[11px]">Marca / Modelo</Label>
                      <Input value={tradeIn.brand} onChange={e => setTradeIn(p => ({...p, brand: e.target.value}))} className="h-8 text-sm"/>
                    </div>
                    <div>
                      <Label className="text-[11px]">Código (opcional)</Label>
                      <Input value={tradeIn.code} onChange={e => setTradeIn(p => ({...p, code: e.target.value}))} className="h-8 text-sm"/>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px]">Estado / Observações</Label>
                      <Input value={tradeIn.condition} onChange={e => setTradeIn(p => ({...p, condition: e.target.value}))} placeholder="Ex: Tela trincada, bateria 80%" className="h-8 text-sm"/>
                    </div>
                    <div>
                      <Label className="text-[11px]">Valor abatido *</Label>
                      <CurrencyInput value={tradeIn.value} onChange={(v) => setTradeIn(p => ({...p, value: v}))} data-testid="trade-in-value"/>
                    </div>
                    <div>
                      <Label className="text-[11px]">Categoria (p/ estoque)</Label>
                      <Select value={tradeIn.category_id || "none"} onValueChange={(v) => setTradeIn(p => ({...p, category_id: v === "none" ? null : v}))}>
                        <SelectTrigger className="h-8 text-xs" data-testid="trade-in-category"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem categoria</SelectItem>
                          {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px]">OBS adicionais</Label>
                      <Input value={tradeIn.notes} onChange={e => setTradeIn(p => ({...p, notes: e.target.value}))} className="h-8 text-sm"/>
                    </div>
                    <label className="col-span-2 flex items-center gap-2 text-xs cursor-pointer pt-1" data-testid="trade-in-add-stock-wrap">
                      <Checkbox checked={tradeIn.add_to_stock} onCheckedChange={(v) => setTradeIn(p => ({...p, add_to_stock: !!v}))} data-testid="trade-in-add-stock"/>
                      Adicionar automaticamente ao estoque <span className="text-neutral-400">(oculto no catálogo)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* ── Taxas extras (não contam para faturamento) ──────── */}
            <div className="border-t border-[#E5E5E5] dark:border-white/10 pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs">Taxas extras <span className="text-[10px] text-neutral-400">(não afetam faturamento/lucro)</span></Label>
                <button
                  type="button"
                  onClick={() => setFees(prev => [...prev, { label: "", amount: 0 }])}
                  className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                  data-testid="add-fee-row"
                ><Plus size={11}/> Adicionar taxa</button>
              </div>
              {fees.length > 0 && (
                <div className="space-y-1.5">
                  {fees.map((f, idx) => (
                    <div key={idx} className="flex gap-2 items-end" data-testid={`fee-row-${idx}`}>
                      <div className="flex-1">
                        <Input value={f.label} onChange={e => setFees(prev => prev.map((x,i)=>i===idx?{...x,label:e.target.value}:x))} placeholder="Ex: Taxa máquina, entrega…" className="h-8 text-sm"/>
                      </div>
                      <div className="w-28"><CurrencyInput value={f.amount} onChange={(v) => setFees(prev => prev.map((x,i)=>i===idx?{...x,amount:v}:x))}/></div>
                      <Button size="icon" variant="ghost" onClick={() => setFees(prev => prev.filter((_,i)=>i!==idx))} className="h-8 w-8 text-red-500"><Trash2 size={12}/></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div><Label>Desconto geral</Label><CurrencyInput value={discount} onChange={(v)=>setDiscount(v)} data-testid="sale-discount-input"/></div>
            <div><Label>Observação</Label><Textarea value={note} onChange={e=>setNote(e.target.value)} rows={2}/></div>
            <div className="flex items-center gap-2"><Checkbox id="snr" checked={showNote} onCheckedChange={(v)=>setShowNote(!!v)}/><Label htmlFor="snr" className="text-xs cursor-pointer">Exibir obs. no recibo</Label></div>
            <div>
              <Label className="text-xs text-neutral-500">Lead <span className="text-neutral-400 font-normal">(origem do cliente — uso interno)</span></Label>
              <LeadSelector value={leadId} onChange={setLeadId} compact />
            </div>
            {lineDiscounts > 0 && (
              <>
                <div className="flex justify-between text-xs text-neutral-500"><span>Itens (sem desconto)</span><span>{brl(subtotalGross)}</span></div>
                <div className="flex justify-between text-xs text-red-600"><span>Desconto nos itens</span><span>-{brl(lineDiscounts)}</span></div>
              </>
            )}
            <div className="flex justify-between text-sm"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
            {Number(discount) > 0 && (
              <div className="flex justify-between text-xs text-red-600"><span>Desconto geral</span><span>-{brl(Number(discount))}</span></div>
            )}
            <div className="flex justify-between font-display text-lg"><span>Total</span><span>{brl(total)}</span></div>
            {feesTotal > 0 && (
              <>
                <div className="flex justify-between text-xs text-amber-700 dark:text-amber-400" data-testid="fees-summary"><span>+ Taxas extras</span><span>{brl(feesTotal)}</span></div>
                <div className="flex justify-between text-sm font-medium border-t border-[#E5E5E5] dark:border-white/10 pt-1.5"><span>Cliente paga</span><span>{brl(grandTotal)}</span></div>
              </>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={()=>submit(false)} data-testid="save-order-button">Salvar pedido</Button>
              <Button className="flex-1 bg-[#0A0A0A] hover:bg-black text-white" onClick={()=>submit(true)} data-testid="finalize-sale-button">Finalizar</Button>
            </div>
          </div>
        </Card>
      </div>

      {showReceipt && lastSale && (
        <Receipt sale={lastSale} store={store} wideLayout onClose={()=>setShowReceipt(false)}/>
      )}
      {showThermal && lastSale && (
        <ThermalReceipt data={lastSale} type="sale" store={store} onClose={()=>setShowThermal(false)}/>
      )}
      {showTradeTerm && tradeIn && (
        <TradeInTerm
          tradeIn={tradeIn}
          customer={customers.find(c => c.id === customerId)}
          store={store}
          onClose={() => setShowTradeTerm(false)}
        />
      )}

      {/* Quick create product (avulso — não vai para estoque) */}
      <Dialog open={creatingProduct} onOpenChange={setCreatingProduct}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Produto avulso (não vai para o estoque)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={newProduct.name} onChange={e=>setNewProduct({...newProduct, name: e.target.value})} data-testid="quick-product-name"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Preço de venda *</Label><CurrencyInput value={newProduct.price} onChange={(v)=>setNewProduct({...newProduct, price: v})} data-testid="quick-product-price"/></div>
              <div><Label>Custo (interno)</Label><CurrencyInput value={newProduct.cost} onChange={(v)=>setNewProduct({...newProduct, cost: v})} data-testid="quick-product-cost"/></div>
            </div>
            <div>
              <Label>Códigos (opcional)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={avulsoCodeDraft}
                  onChange={e=>setAvulsoCodeDraft(e.target.value)}
                  onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addAvulsoCode(avulsoCodeDraft); setAvulsoCodeDraft(""); } }}
                  placeholder="Digite e Enter, ou bipe — aparece no recibo"
                  className="font-mono"
                  data-testid="quick-product-code"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={()=>{ if (avulsoCodeDraft.trim()) { addAvulsoCode(avulsoCodeDraft); setAvulsoCodeDraft(""); } }}
                  title="Adicionar código digitado"
                  data-testid="quick-product-code-add"
                ><Plus size={14}/></Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={()=>setScanAvulsoOpen(true)}
                  title="Ler código com a câmera"
                  data-testid="quick-product-scan"
                >
                  <ScanLine size={14} className="mr-1"/> Ler
                </Button>
              </div>
              {(newProduct.codes || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(newProduct.codes || []).map((c, idx) => (
                    <div key={idx} className="inline-flex items-center gap-1 bg-neutral-100 border border-[#E5E5E5] rounded-md pl-2 pr-1 py-1 text-xs">
                      <input
                        value={c}
                        onChange={(e)=>editAvulsoCode(idx, e.target.value)}
                        className="bg-transparent border-0 outline-0 text-xs font-mono min-w-[70px] max-w-[180px]"
                        style={{ width: `${Math.max(70, (c?.length || 0) * 8)}px` }}
                        data-testid={`quick-product-code-item-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={()=>removeAvulsoCode(idx)}
                        className="ml-0.5 text-neutral-400 hover:text-red-600"
                        title="Remover este código"
                        data-testid={`quick-product-code-remove-${idx}`}
                      ><X size={12}/></button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-neutral-500 mt-1">Você pode adicionar quantos códigos quiser. Todos aparecem no recibo.</p>
            </div>
            <div>
              <Label>Fornecedor (opcional)</Label>
              <Select value={newProduct.supplier_id || "none"} onValueChange={(v)=>{
                const supId = v === "none" ? null : v;
                setNewProduct({
                  ...newProduct,
                  supplier_id: supId,
                  supplier_note: "",
                  // Pré-preenche data com HOJE ao selecionar fornecedor
                  supplier_purchase_date: supId ? (newProduct.supplier_purchase_date || new Date().toISOString().slice(0, 10)) : null,
                });
              }}>
                <SelectTrigger data-testid="quick-product-supplier"><SelectValue placeholder="Selecione um fornecedor"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem fornecedor</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newProduct.supplier_id && (
              <PurchaseDatePicker
                value={newProduct.supplier_purchase_date}
                onChange={(v)=>setNewProduct({...newProduct, supplier_purchase_date: v})}
                testIdPrefix="quick-product-purchase-date"
              />
            )}
            {(() => {
              const sup = suppliers.find(s => s.id === newProduct.supplier_id);
              if (sup && /troca/i.test(sup.name || "")) {
                return (
                  <div>
                    <Label>Observação (Troca)</Label>
                    <Input
                      value={newProduct.supplier_note}
                      onChange={e=>setNewProduct({...newProduct, supplier_note: e.target.value})}
                      placeholder="Ex.: produto recebido na troca do cliente Martin"
                      data-testid="quick-product-supplier-note"
                    />
                  </div>
                );
              }
              return null;
            })()}
            <p className="text-[11px] text-neutral-500">Esse produto será incluído apenas nesta venda — não é cadastrado no estoque.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreatingProduct(false)}>Cancelar</Button>
            <Button onClick={submitNewProduct} disabled={productBusy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="quick-product-submit">
              {productBusy ? "Adicionando…" : "Adicionar à venda"}
            </Button>
          </DialogFooter>

          {/* Scanner nested DENTRO do diálogo do avulso. Radix Dialog tem
              comportamento de fechar o pai quando um sibling modal abre por
              causa do roubo de foco. Aninhar resolve. */}
          <BarcodeScanner
            open={scanAvulsoOpen}
            continuous={true}
            onClose={()=>setScanAvulsoOpen(false)}
            onDetect={(code)=>addAvulsoCode(code)}
            title="Ler códigos do produto avulso"
          />
        </DialogContent>
      </Dialog>

      {/* Quick create customer */}
      <Dialog open={creatingCustomer} onOpenChange={setCreatingCustomer}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Novo cliente rápido</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={newCustomer.name} onChange={e=>setNewCustomer({...newCustomer, name: e.target.value})} data-testid="quick-customer-name"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefone</Label><Input value={newCustomer.phone} onChange={e=>setNewCustomer({...newCustomer, phone: e.target.value})} placeholder="(00) 00000-0000" data-testid="quick-customer-phone"/></div>
              <div><Label>CPF</Label><Input value={newCustomer.cpf} onChange={e=>setNewCustomer({...newCustomer, cpf: e.target.value})} placeholder="000.000.000-00" data-testid="quick-customer-cpf"/></div>
            </div>
            <div><Label>Endereço</Label><Input value={newCustomer.address} onChange={e=>setNewCustomer({...newCustomer, address: e.target.value})} data-testid="quick-customer-address"/></div>
            <p className="text-[11px] text-neutral-500">Apenas o nome é obrigatório. Os demais campos são opcionais.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreatingCustomer(false)}>Cancelar</Button>
            <Button onClick={submitNewCustomer} disabled={customerBusy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="quick-customer-submit">
              {customerBusy ? "Criando…" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {variantTarget && (
        <VariantPicker
          product={variantTarget}
          initialQuantities={currentVariantQuantities(variantTarget.id)}
          confirmLabel="Adicionar ao carrinho"
          showCost={true}
          onConfirm={(selections) => applyVariantSelections(variantTarget, selections)}
          onClose={() => setVariantTarget(null)}
        />
      )}

      <ConfirmationDialog
        open={!!confirmation}
        title={confirmation?.title}
        description={confirmation?.description}
        onPrintReceipt={confirmation?.canPrint && lastSale ? () => {
          setConfirmation(null);
          setShowThermal(true);
        } : undefined}
        onClose={() => setConfirmation(null)}
      />

      <BarcodeScanner
        open={scanOpen}
        continuous={true}
        onClose={()=>setScanOpen(false)}
        onDetect={(code)=>handleCodeLookup(code)}
        title="Bipar produtos — leitura contínua"
      />

      <BarcodeScanner
        open={!!scanCartTarget}
        continuous={true}
        onClose={()=>setScanCartTarget(null)}
        onDetect={(code)=>{
          // IMPORTANTE: salva o código APENAS no item do carrinho (escopo da venda).
          // NÃO modifica o produto cadastrado — atende ao pedido do Lojista.
          if (scanCartTarget?.key) addCodeToCartItem(scanCartTarget.key, code);
        }}
        title="Bipar códigos para esta venda"
      />

      <InstallmentPlanModal
        open={planModalOpen}
        onClose={()=>setPlanModalOpen(false)}
        totalAmount={programmedTotal}
        initialPlan={installmentPlan}
        customerName={customers.find(c => c.id === customerId)?.name}
        onSave={(plan)=>{ setInstallmentPlan(plan); setPlanModalOpen(false); toast.success("Parcelamento configurado"); }}
      />
    </div>
  );
}

/**
 * PurchaseDatePicker — botão "Hoje" (padrão) + "Outra" (abre input date).
 * Mesmo componente usado em Produtos.jsx; copiado aqui para evitar cross-import.
 */
function PurchaseDatePicker({ value, onChange, testIdPrefix = "purchase-date" }) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = React.useState((!value || value === today) ? "today" : "other");
  React.useEffect(() => {
    setMode(value && value !== today ? "other" : "today");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div>
      <Label>Data da compra <span className="text-[10px] text-neutral-400 font-normal">(interno)</span></Label>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <div className="inline-flex rounded-md border border-[#E5E5E5] overflow-hidden text-sm">
          <button
            type="button"
            className={`px-3 py-1.5 ${mode === "today" ? "bg-[#0A0A0A] text-white" : "bg-white dark:bg-white/5 hover:bg-neutral-50"}`}
            onClick={() => { setMode("today"); onChange(today); }}
            data-testid={`${testIdPrefix}-today`}
          >Hoje</button>
          <button
            type="button"
            className={`px-3 py-1.5 border-l border-[#E5E5E5] ${mode === "other" ? "bg-[#0A0A0A] text-white" : "bg-white dark:bg-white/5 hover:bg-neutral-50"}`}
            onClick={() => setMode("other")}
            data-testid={`${testIdPrefix}-other`}
          >Outra</button>
        </div>
        {mode === "other" && (
          <Input
            type="date"
            value={value || today}
            onChange={(e) => onChange(e.target.value)}
            className="w-40 h-9"
            data-testid={`${testIdPrefix}-input`}
          />
        )}
        {mode === "today" && (
          <span className="text-xs text-neutral-500">{new Date(today + "T00:00:00").toLocaleDateString('pt-BR')}</span>
        )}
      </div>
    </div>
  );
}

